import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import Autocomplete from 'components/autocomplete';
import { Star, TriangleAlert } from 'lucide-react';
import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'underscore';
import { createFuzzyFilterOptions } from 'utils/fuzzy-filter';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import { getOrderDateParts, isDateWithinProductRange } from 'views/order-feed/features/my-supply-plan/utils/order-utils';
import { CustomerAssortmentProduct, CustomerAssortment } from 'views/order-feed/types/customer-assortment.type';

const PAGE_NAME_ID = 'orderFeed.listOrderSupplyPlan.orderList';
const CREATE_ORDER_ID = `${PAGE_NAME_ID}.createOrder`;

// #region Product option helpers
// Move to order-utils if edit-order reuses this option-building shape.
type ProductOption = CustomerAssortmentProduct & {
  preferred: boolean;
};

type ProductOptionEntry = [string, ProductOption];
type ProductOptions = {
  productOptions: ProductOption[];
  productOptionsByItemNumber: Record<string, ProductOption>;
  itemNumberOptions: string[];
};

const getProductOptionEntries = (sourceProducts: CustomerAssortmentProduct[] | undefined, preferred: boolean): ProductOptionEntry[] =>
  sourceProducts?.map((product) => [product.itemNumber, { ...product, preferred }]) ?? [];

const buildProductOptions = (products: CustomerAssortment | undefined): ProductOptions => {
  // Preferred products are spread last so duplicate item numbers render as preferred.
  const combinedProducts = new Map([
    ...getProductOptionEntries(products?.availableProducts, false),
    ...getProductOptionEntries(products?.preferredProducts, true),
  ]);
  const productOptions = _.chain(Array.from(combinedProducts.values()))
    .sortBy('itemName')
    .reverse()
    .sortBy('preferred')
    .reverse()
    .value();

  return {
    productOptions,
    productOptionsByItemNumber: _.indexBy(productOptions, 'itemNumber'),
    itemNumberOptions: productOptions.map((product) => product.itemNumber),
  };
};
// #endregion Product option helpers

type Props = {
  idx: number;
  products: CustomerAssortment | undefined;
  forecastProductNames?: Record<string, string>;
};

const PAGE_ID = 'orderFeed.ofdMySupplyPlan.orders';

const ProductAutocomplete: React.FC<Props> = ({ idx, products, forecastProductNames }) => {
  const intl = useIntl();
  const theme = useTheme();
  const { control, trigger } = useFormContext<CreateOrderFormValues>();

  const orderLines = useWatch({ control, name: 'orderLines' });
  const requestedDispatchDateValue = useWatch({ control, name: 'requestedDispatchDate' });

  const { productOptions, productOptionsByItemNumber, itemNumberOptions } = useMemo(() => buildProductOptions(products), [products]);

  const requestedDispatchDate = getOrderDateParts(requestedDispatchDateValue || '').requestedDispatchDate;
  const productLabel = intl.formatMessage({ id: `${PAGE_NAME_ID}.productLabel` });
  const requiredMessage = intl.formatMessage({ id: 'form.rules.required' });
  const preferredProductsLabel = intl.formatMessage({ id: `${CREATE_ORDER_ID}.preferredProductsGroup` });
  const allProductsLabel = intl.formatMessage({ id: `${CREATE_ORDER_ID}.availableProductsGroup` });

  const getProductLabel = (itemNumber: string): string =>
    productOptionsByItemNumber[itemNumber]?.itemName ?? forecastProductNames?.[itemNumber] ?? itemNumber;

  const currentItemNumber = orderLines?.[idx]?.itemNumber;
  const isUnavailable = !!currentItemNumber && !productOptionsByItemNumber[currentItemNumber];

  const filterOptions = useMemo(
    () =>
      createFuzzyFilterOptions<string>((itemNumber) => {
        const product = productOptionsByItemNumber[itemNumber];
        return [product?.itemName, itemNumber].filter(Boolean).join(' ');
      }),
    [productOptionsByItemNumber]
  );

  return (
    <Autocomplete<string>
      dark
      fullWidth
      openOnFocus
      label={productLabel}
      startAdornment={
        isUnavailable ? (
          <Tooltip title={<FormattedMessage id={`${PAGE_ID}.list.body.productNotAvailableTooltipMessage`} />}>
            <TriangleAlert fill={theme.palette.orange.main} strokeWidth={1} stroke={theme.palette.darkBlue.main} size={20} />
          </Tooltip>
        ) : undefined
      }
      disableCloseOnSelect={false}
      options={itemNumberOptions}
      isOptionEqualToValue={(option, value) => option === value}
      onChange={() => trigger(`orderLines.${idx}.itemNumber`)}
      getOptionLabel={getProductLabel}
      filterOptions={filterOptions}
      getOptionDisabled={(option) => orderLines?.some((line, lineIdx) => lineIdx !== idx && line.itemNumber === option)}
      groupBy={(option) => (productOptionsByItemNumber[option]?.preferred ? preferredProductsLabel : allProductsLabel)}
      renderOption={(props, option) => {
        const product = productOptionsByItemNumber[option];
        const isSelected = option === orderLines[idx].itemNumber;
        const preferredStarFill = isSelected ? theme.palette.darkBlue.main : theme.palette.common.white;

        return (
          <Box {...props} key={option} component="li" display="flex" alignItems="center" gap={2}>
            {product.preferred && <Star size={20} fill={preferredStarFill} style={{ flexShrink: 0 }} />}
            <Box display="flex" flexDirection="column">
              <Typography variant="caption" color="darkBlue.main">
                {product.itemName}
              </Typography>
              <Typography fontSize={14} variant="caption" color="darkBlue.o50">
                {Number(product.itemNumber)}
              </Typography>
            </Box>
          </Box>
        );
      }}
      controller={{
        name: `orderLines.${idx}.itemNumber`,
        control,
        rules: {
          required: requiredMessage,
          validate: {
            available: (itemNumber: string) =>
              !itemNumber ||
              !!productOptionsByItemNumber[itemNumber] ||
              intl.formatMessage({ id: `${PAGE_ID}.list.body.productNotAvailable` }),
            valid: (itemNumber: string) => isDateWithinProductRange(productOptions, itemNumber, requestedDispatchDate),
          },
        },
      }}
    />
  );
};

export default ProductAutocomplete;
